import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import Heading from '@cotyorg/ccx-atoms/Heading';
import OptionalSection from '@cotyorg/ccx-utils/js/components/OptionalSection';
import NumberOfRetailers from '../NumberOfRetailers';
import ProductSteps from '../ProductSteps';
import { isSmallBreakpoint } from '../../utils';
import ProductSelection from '../ProductSelection';

const ProductInfo = ({
  sectionHeadings,
  productDetails,
  currentBreakpoint,
  subcategoryTitle,
  categoryTitle,
  retailerInfo,
  basketProduct,
  swatches,
  presentationLocale,
  showStarRating,
  customProductBuyLink,
  hideNumberOfRetailers,
  hidePrice,
}) => (
  <div className="product-info flex-container flex-dir-column">
    {!isSmallBreakpoint({ currentBreakpoint }) && (
      <>
        <Heading
          extraClass="h2 product-info__heading"
          level={1}
          data-test="product-heading"
        >
          {R.path(['productDetails', 'heading'], sectionHeadings)}
        </Heading>

        <OptionalSection
          renderCondition={showStarRating}
          section={() => (
            <div
              data-bv-show="rating_summary"
              data-bv-product-id={R.prop('bvid', productDetails)}
            />
          )}
        />

        <OptionalSection
          renderCondition={!hideNumberOfRetailers}
          section={() => <NumberOfRetailers />}
        />

        {R.prop('descriptionHTML', productDetails) ? (
          <div
            className="product-info__description"
            data-test="product-description"
            dangerouslySetInnerHTML={{
              __html: R.prop('descriptionHTML', productDetails),
            }}
          />
        ) : (
          <div
            className="product-info__description"
            data-test="product-description"
          >
            {R.prop('description', productDetails)}
          </div>
        )}

        <ProductSteps />
      </>
    )}

    <ProductSelection
      swatches={swatches}
      productDetails={productDetails}
      subcategoryTitle={subcategoryTitle}
      categoryTitle={categoryTitle}
      basketProduct={basketProduct}
      retailerInfo={retailerInfo}
      presentationLocale={presentationLocale}
      customProductBuyLink={customProductBuyLink}
      hidePrice={hidePrice}
    />
  </div>
);

ProductInfo.propTypes = {
  sectionHeadings: PropTypes.shape({
    relatedProducts: PropTypes.shape({
      heading: PropTypes.string,
    }),
    howToSteps: PropTypes.shape({
      heading: PropTypes.string,
    }),
    retailers: PropTypes.shape({
      heading: PropTypes.string,
    }),
  }),
  productDetails: PropTypes.shape({
    averageRating: PropTypes.any,
    benefits: PropTypes.arrayOf(PropTypes.any),
    buyButtonLabel: PropTypes.string,
    bvid: PropTypes.string,
    description: PropTypes.string,
    fromRetailersLabel: PropTypes.string,
    howToSteps: PropTypes.arrayOf(PropTypes.any),
    showIngredients: PropTypes.string,
    hideIngredients: PropTypes.string,
    longDescription: PropTypes.string,
    noReviewsLabel: PropTypes.string,
    numberOfReviews: PropTypes.number,
    outOfStockLabel: PropTypes.string,
    priceRetailersPluralLabel: PropTypes.string,
    priceRetailersSingularLabel: PropTypes.string,
    quantityLabel: PropTypes.string,
    readReviewsLabel: PropTypes.string,
    retailersNotFoundLabel: PropTypes.string,
    shadeSelectLabel: PropTypes.string,
    title: PropTypes.string,
  }),
  currentBreakpoint: PropTypes.string,
  subcategoryTitle: PropTypes.string,
  categoryTitle: PropTypes.string,
  retailerInfo: PropTypes.shape({
    numberOfRetailers: PropTypes.number,
    lowestPrice: PropTypes.shape({
      currencySymbol: PropTypes.string,
      number: PropTypes.number,
      price: PropTypes.string,
    }),
    inStock: PropTypes.bool,
  }),
  basketProduct: PropTypes.shape({
    name: PropTypes.string,
    shadeName: PropTypes.string,
    price: PropTypes.number,
    currency: PropTypes.string,
    image: PropTypes.string,
    ean: PropTypes.string,
    retailers: PropTypes.array,
    sku: PropTypes.string,
    productId: PropTypes.string,
  }),
  swatches: PropTypes.arrayOf(
    PropTypes.shape({
      sku: PropTypes.string,
      itemName: PropTypes.string,
      skuColor: PropTypes.string,
    })
  ),
  presentationLocale: PropTypes.string.isRequired,
  showStarRating: PropTypes.bool.isRequired,
  customProductBuyLink: PropTypes.string,
  hideNumberOfRetailers: PropTypes.bool,
  hidePrice: PropTypes.bool,
};

export default ProductInfo;
